import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { QUERY_KEYS_NO_FETCHING as QUERY_KEYS } from 'utils/constants';
import { PunchupAnalyticsSection } from 'features/common/components/Punchup/primitives/charts/PunchupAnalyticsSection';
import { PunchupRefreshButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupPageContent } from 'features/common/components/Punchup/primitives/layout/PunchupPageContent';
import { PunchupPageHeader } from 'features/common/components/Punchup/primitives/layout/PunchupPageHeader';
import { PunchupErrorState } from 'features/common/components/Punchup/primitives/feedback/PunchupErrorState';
import { PunchupPageShell } from 'features/common/components/Punchup/pages/PunchupPageShell';
import {
	CHART_HOLD_COLOR,
	CHART_COMP_COLOR,
	CHART_POOL_COLORS,
	generatePoolColor,
} from 'features/common/utils/chartColors';
import { buildSeatsCategoryColorMap, buildTicketTypeColorMap } from 'features/common/utils/ticketTypeColors';
import { useGetSeatsIOEvent } from 'hooks/useGetSeatsIOEvent';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { TicketType } from 'features/common/types';
import { useHighlights } from 'features/common/hooks/useHighlights';
import { IdParamsType } from 'utils/types';

import { AnalyticsSeatedProvider } from './analyticsContext';
import { PunchupDayOfWeekSection } from './PunchupDayOfWeekSection';
import { PunchupNetSalesByTypeSection } from './PunchupNetSalesByTypeSection';
import { PunchupShowPerformanceSection } from './PunchupShowPerformanceSection';
import { PunchupVenueOperationsSection } from './PunchupVenueOperationsSection';
import NetSalesChartSection from './PunchupNetSalesSection/PunchupNetSalesSection';
import PunchupTicketInventory from './PunchupTicketInventorySection/PunchupTicketInventory';
import TicketsSoldChartSection from './PunchupTicketsSoldSection/PunchupTicketsSoldSection';
import styles from './PunchupAnalytics.module.css';

export interface ChartPalette {
	/**
	 * Ticket-type name → chart color, from the shared `buildTicketTypeColorMap` (a stored colour or a
	 * stable non-orange palette fallback) — the SAME map the table tags, pool member chips, and tile
	 * rails read, so a tier reads one colour across the whole iframe.
	 */
	ticketTypeColors: Record<string, string>;
	/**
	 * Pool name → chart color. Assigned from the separate CHART_POOL_COLORS terracotta
	 * palette (warm browns) so pool and ticket-type colors never collide regardless of
	 * how many types the event has. Pools beyond 8 use generatePoolColor().
	 */
	poolColors: Record<string, string>;
	/**
	 * Fallback color for anonymous hold segments (poolName === null).
	 * Fixed constant so it never collides with ticket-type or pool colors.
	 */
	onHoldColor: string;
	/**
	 * Fixed color for "Comped" segments in the summary donut.
	 * Same rationale as onHoldColor — fixed, not derived from remaining palette entries.
	 */
	compedColor: string;
}

/**
 * Builds the analytics color palette. Ticket-type colours come from the shared
 * `buildTicketTypeColorMap` (a stored `TicketType.Color` when set + unique, else a stable non-orange
 * palette fallback) — the SAME map the table tags, pool member chips, and tile rails read, so a tier
 * reads one colour across the whole iframe and the "no two on-screen series share a colour" invariant
 * holds. Pool colours use a separate terracotta palette; on-hold and comped slots use fixed tokens that
 * never overlap the series.
 */
export function buildChartPalette(
	ticketTypes: TicketType[],
	seatsCategoryColors?: Record<string, string>
): ChartPalette {
	// Seated events overlay the bound seats.io category colour (the display source of truth); empty/omitted
	// for non-seated, so the chart series read the same stored-colour/palette map as everywhere else.
	const ticketTypeColors = buildTicketTypeColorMap(ticketTypes, seatsCategoryColors);
	// Pool colors use a separate terracotta palette — no collision with ticket-type colours possible.
	const poolNames: string[] = [];
	const seenPools = new Set<string>();
	for (const tt of ticketTypes) {
		const name = tt.ticketsPool?.name;
		if (name && !seenPools.has(name)) {
			seenPools.add(name);
			poolNames.push(name);
		}
	}
	const poolColors: Record<string, string> = {};
	poolNames.forEach((name, i) => {
		poolColors[name] = i < CHART_POOL_COLORS.length ? CHART_POOL_COLORS[i] : generatePoolColor(i);
	});
	return {
		ticketTypeColors,
		poolColors,
		onHoldColor: CHART_HOLD_COLOR,
		compedColor: CHART_COMP_COLOR,
	};
}

export const PunchupAnalytics: FC = () => {
	const { id } = useParams<IdParamsType>();
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	// useGetEvent shares the ['event', id] React Query cache — no extra network request.
	const { event, isError: isEventError } = useGetEvent();
	// Called here so refresh invalidation triggers re-fetches in child sections
	// (shares the same React Query cache key — no extra network request). The Refresh
	// button spins on `isFetching` (true during a cache-warm background refetch); the
	// sections' own `isLoading` stays false on that refetch so charts don't blank.
	const { isFetching: isRefreshing } = useHighlights(id, true);

	// Seated events: feed the bound seats.io category colours into the palette so the charts match the
	// rest of the iframe (and the seats chart). Shared query key → no extra fetch; non-seated → no fetch.
	const { data: seatsEvent } = useGetSeatsIOEvent(event?.seatsEventKey);
	// Build palette from ALL ticket types (not just those with activity) so zero-interaction
	// types get a color assignment that remains stable as activity accumulates.
	const { ticketTypeColors, poolColors, onHoldColor, compedColor } = useMemo(
		() =>
			buildChartPalette(
				event?.ticketTypes ?? [],
				buildSeatsCategoryColorMap(event?.ticketTypes ?? [], seatsEvent?.categories)
			),
		[event?.ticketTypes, seatsEvent?.categories]
	);

	// Invalidate EVERY key the page's sections read so they all re-fetch in lockstep. EVENT_HIGHLIGHTS backs the
	// KPIs + Sales-Trends charts; scan-history the Show-up Rate + Show-Day arrivals; comped-inventory the
	// revenue-weighted Comp Rate + per-type Comped donut; ticketTypes + eventPools the pool-aware capacity
	// (Fill Rate denominator + Ticket-Inventory amounts/holds/pool totals) read via useEventCapacity. Omitting
	// any one leaves that slice of the page stale while the rest refreshes — the capacity keys were the gap.
	const handleRefresh = () => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EVENT_HIGHLIGHTS, id] });
		queryClient.invalidateQueries({ queryKey: ['scan-history', id] });
		queryClient.invalidateQueries({ queryKey: ['comped-inventory', id] });
		queryClient.invalidateQueries({ queryKey: ['ticketTypes', id] });
		queryClient.invalidateQueries({ queryKey: ['eventPools', id] });
	};

	// Retry from the error state: re-fetch the foundational event query too, then everything else.
	const handleRetry = () => {
		queryClient.invalidateQueries({ queryKey: ['event', id] });
		handleRefresh();
	};

	// A failed event load with no cached data is the catastrophic case — surface a retryable error
	// instead of an indefinite spinner / empty sections. A partial failure (e.g. scans only) still
	// degrades gracefully per section.
	const isErrorNoData = isEventError && !event;

	return (
		<PunchupPageShell pageName="Analytics">
			<PunchupPageContent>
				{/* Single page-wide seated check — seated events have no pools, so pool copy is suppressed
				    everywhere downstream via useIsSeatedEvent (see analyticsContext). */}
				<AnalyticsSeatedProvider value={!!event?.seatsEventKey}>
					<div className={styles.contentConstraint}>
						<PunchupPageHeader
							title="Analytics"
							description="View sales performance and attendance data for this show."
							actions={
								<PunchupRefreshButton onClick={handleRefresh} isLoading={isRefreshing}>
									{t('refresh')}
								</PunchupRefreshButton>
							}
						/>
						{isErrorNoData ? (
							<PunchupErrorState
								title={t('analytics-load-error-title', "We couldn't load this show's analytics")}
								description={t(
									'analytics-load-error-desc',
									'Something went wrong while fetching the data. Try again in a moment.'
								)}
								onRetry={handleRetry}
								retryLabel={t('button-retry', 'Try again')}
							/>
						) : (
							<>
								<PunchupShowPerformanceSection />
								<PunchupAnalyticsSection
									title={t('sales')}
									description={t('analytics-sales-description')}
									rows={[
										{
											items: [
												<TicketsSoldChartSection key="tickets-sold" ticketTypeColors={ticketTypeColors} />,
												<PunchupNetSalesByTypeSection key="net-sales-by-type" ticketTypeColors={ticketTypeColors} />,
											],
										},
										{
											items: [
												<NetSalesChartSection key="revenue" />,
												<PunchupDayOfWeekSection key="day-of-week" ticketTypeColors={ticketTypeColors} />,
											],
										},
									]}
								/>
								<PunchupTicketInventory
									ticketTypeColors={ticketTypeColors}
									poolColors={poolColors}
									onHoldColor={onHoldColor}
									compedColor={compedColor}
								/>
								<PunchupVenueOperationsSection />
							</>
						)}
					</div>
				</AnalyticsSeatedProvider>
			</PunchupPageContent>
		</PunchupPageShell>
	);
};
